import React, { useState, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ImageCropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  onSave: (cropData: CropData, viewportPosition: { x: number; y: number }) => Promise<void>;
  isProcessing?: boolean;
  initialCrop?: CropData | null;
  initialViewport?: { x: number; y: number } | null;
}

interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function ImageCropDialog({
  open,
  onOpenChange,
  imageUrl,
  onSave,
  isProcessing = false,
  initialCrop = null,
  initialViewport = null,
}: ImageCropDialogProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropData>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number>(300);

  // Calculate max height (50% of viewport height)
  useEffect(() => {
    const calculateMaxHeight = () => {
      const viewportHeight = window.innerHeight;
      const newMaxHeight = Math.max(viewportHeight * 0.5, 300); // Min 300px, max 50% of viewport
      setMaxHeight(newMaxHeight);
    };

    calculateMaxHeight();
    window.addEventListener('resize', calculateMaxHeight);
    return () => window.removeEventListener('resize', calculateMaxHeight);
  }, []);

  // Load initial crop data when dialog opens
  useEffect(() => {
    if (open) {
      // Restore the exact viewport position (or reset to center)
      setCrop(initialViewport || { x: 0, y: 0 });
      // Don't reset croppedAreaPixels here - let onCropComplete handle it
    } else {
      // Reset everything when dialog closes
      setCrop({ x: 0, y: 0 });
      setCroppedAreaPixels({ x: 0, y: 0, width: 0, height: 0 });
    }
  }, [open, initialViewport]);

  const handleCropComplete = (_croppedArea: unknown, croppedAreaPixels: CropData) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleSave = async () => {
    if (croppedAreaPixels.width === 0 || croppedAreaPixels.height === 0) return;

    try {
      setIsSaving(true);
      await onSave(croppedAreaPixels, crop);
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving && !isProcessing) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Optimizar foto del plato</DialogTitle>
          <DialogDescription>
            Ajusta el área de la foto que quieres usar. Se optimizará automáticamente al formato Instagram Reels (9:16).
            {initialCrop && ' Estás editando un crop existente.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Aspect ratio info */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Aspecto: Instagram Reels (9:16)</span>
          </div>

          {/* Image Preview with Crop */}
          <div
            className="relative w-full bg-black rounded-lg overflow-hidden"
            style={{ aspectRatio: '9/16', maxHeight: `${maxHeight}px` }}
          >
            <Cropper
              image={imageUrl}
              crop={crop}
              zoom={1}
              aspect={9 / 16}
              cropShape="rect"
              showGrid={true}
              onCropChange={setCrop}
              onCropComplete={handleCropComplete}
              restrictPosition={true}
              objectFit="contain"
            />
          </div>

          {/* Info text */}
          <div className="text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded p-3">
            <p>Visualizando con aspecto <strong>9:16</strong> (Instagram Reels). La imagen será redimensionada a 1080x1920px.</p>
          </div>

          {/* Crop Data Display */}
          {croppedAreaPixels && croppedAreaPixels.width > 0 && croppedAreaPixels.height > 0 ? (
            <div className="text-xs text-gray-700 bg-gray-100 p-3 rounded border border-gray-200">
              <div className="font-medium mb-1">Área seleccionada:</div>
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <span className="text-gray-500">X:</span> {Math.round(croppedAreaPixels.x || 0)}px
                </div>
                <div>
                  <span className="text-gray-500">Y:</span> {Math.round(croppedAreaPixels.y || 0)}px
                </div>
                <div>
                  <span className="text-gray-500">Ancho:</span> {Math.round(croppedAreaPixels.width || 0)}px
                </div>
                <div>
                  <span className="text-gray-500">Alto:</span> {Math.round(croppedAreaPixels.height || 0)}px
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSaving || isProcessing}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={croppedAreaPixels.width === 0 || croppedAreaPixels.height === 0 || isSaving || isProcessing}
          >
            {isSaving || isProcessing ? 'Optimizando...' : initialCrop ? 'Actualizar crop' : 'Guardar cambios'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
